import React, { useContext, useEffect, useRef, useState } from 'react';
import UserFeedbackModal from './UserFeedbackModal';
import { PermissionsContext } from '../../../Contexts/Permissions';
import UserFeedbackService from '../../../Services/userFeedback.service';
import {
  FEEDBACK_TIMING,
  trackFeedbackSession,
  shouldAutoPromptFeedback,
  markFeedbackAutoShown,
} from '../../../Utils/userFeedbackTiming';

/**
 * Mount once in MainLayout. Tracks sessions and may auto-open feedback modal.
 */
export default function UserFeedbackPromptHost() {
  const { userId, token } = useContext(PermissionsContext);
  const [open, setOpen] = useState(false);
  const [isAuto, setIsAuto] = useState(false);
  const timerRef = useRef(null);
  const checkedRef = useRef(false);

  useEffect(() => {
    if (!userId || !token) return;
    trackFeedbackSession(userId);
  }, [userId, token]);

  useEffect(() => {
    if (!userId || !token || checkedRef.current) return;
    checkedRef.current = true;

    let cancelled = false;
    (async () => {
      try {
        const eligibility = await UserFeedbackService.getEligibility(token);
        if (cancelled) return;
        const payload = {
          hasSubmittedBefore: eligibility?.hasSubmittedBefore ?? eligibility?.HasSubmittedBefore,
          canAutoPrompt: eligibility?.canAutoPrompt ?? eligibility?.CanAutoPrompt,
          accountAgeDays: eligibility?.accountAgeDays ?? eligibility?.AccountAgeDays,
          loginSessionCount: eligibility?.loginSessionCount ?? eligibility?.LoginSessionCount,
        };
        if (!shouldAutoPromptFeedback(userId, payload)) {
          return;
        }
        timerRef.current = setTimeout(() => {
          if (cancelled) return;
          markFeedbackAutoShown(userId);
          setIsAuto(true);
          setOpen(true);
        }, FEEDBACK_TIMING.AUTO_DELAY_MS);
      } catch {
        /* ignore */
      }
    })();

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [userId, token]);

  return (
    <UserFeedbackModal
      open={open}
      isAuto={isAuto}
      token={token}
      userId={userId}
      source="Web"
      onClose={() => {
        setOpen(false);
        setIsAuto(false);
      }}
    />
  );
}

/** Expose imperatively via custom event for UpperPanelDetails */
export function openUserFeedbackModal() {
  window.dispatchEvent(new CustomEvent('xtram:open-user-feedback'));
}

export function UserFeedbackManualListener() {
  const { userId, token } = useContext(PermissionsContext);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener('xtram:open-user-feedback', handler);
    return () => window.removeEventListener('xtram:open-user-feedback', handler);
  }, []);

  return (
    <UserFeedbackModal
      open={open}
      isAuto={false}
      token={token}
      userId={userId}
      source="Web"
      onClose={() => setOpen(false)}
    />
  );
}
